import { ReactNode, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Flex, Group, Loader, Stack, Text } from '@mantine/core';
import { IconDownload, IconPencil } from '@tabler/icons-react';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { Button } from 'src/design-system/Button';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { useCalendarEventMeetingAgenda } from 'src/meetings/hooks/useCalendarEventMeetingAgenda';
import { useGenerateCalendarEventMeetingAgenda } from 'src/meetings/hooks/useGenerateCalendarEventMeetingAgenda';
import { showError, showSuccess } from 'src/notifications/notifications';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Logger } from 'src/utils/Logger';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MeetingDetailsCard } from './MeetingDetailsCard';

type Props = {
  eventId: string;
  hasMeetingAgenda?: boolean;
  mainMemberName: string;
  familyName: string;
  onEditMembers?: () => void;
  onGenerateFamily?: () => void;
};

export function MeetingAgendaCard({
  eventId,
  hasMeetingAgenda,
  mainMemberName,
  familyName,
  onEditMembers,
  onGenerateFamily,
}: Props) {
  const { files: templates } = useTemplates({ templateType: 'MEETING_AGENDA' });
  const defaultTemplate = templates.find(template => template.isDefault) ?? templates[0];

  const {
    generateMeetingAgenda,
    isLoading: isGenerating,
    isSuccess,
  } = useGenerateCalendarEventMeetingAgenda(eventId);
  const { meetingAgenda } = useCalendarEventMeetingAgenda({
    eventId,
    enabled: !!hasMeetingAgenda || isSuccess,
  });
  const { createLink, isLoading: isDownloading } = useFileTemporaryLinkMutation();
  const navigate = useNavigate();

  const status = meetingAgenda?.backgroundProcess?.status;
  const isProcessing = isGenerating || status === 'PENDING' || status === 'PROCESSING';
  const isReady = status === 'SUCCESS' && !!meetingAgenda?.markdown;

  const previousStatus = useRef(status);
  useEffect(() => {
    if (status === 'SUCCESS' && previousStatus.current && previousStatus.current !== 'SUCCESS') {
      showSuccess('Meeting agenda generated');
    }
    previousStatus.current = status;
  }, [status]);

  const handleGenerateIndividual = async () => {
    if (!defaultTemplate) return;
    try {
      await generateMeetingAgenda({ templateId: defaultTemplate.id });
    } catch (error) {
      Logger.error('Failed to generate meeting agenda', error, {
        eventId,
        templateId: defaultTemplate.id,
      });
      showError('Could not generate the meeting agenda. Please try again.');
    }
  };

  const handleDownload = async () => {
    if (meetingAgenda?.fileId == null) return;
    try {
      const link = await createLink(meetingAgenda.fileId);
      window.open(link.url, '_blank');
    } catch (error) {
      notifyAndLogError(
        'Failed to download meeting agenda',
        'Could not download the meeting agenda. Please try again.',
        error,
      );
    }
  };

  const handleEditAgenda = () => {
    if (!meetingAgenda?.id || !meetingAgenda.clientId) return;
    navigate(`/client/${meetingAgenda.clientId}/document/meeting-agendas/${meetingAgenda.id}/edit`);
  };

  if (isReady) {
    return (
      <MeetingDetailsCard
        title="Meeting agenda"
        headerRight={
          <Group gap="xs" wrap="nowrap">
            <Button
              variant="secondary"
              size="sm"
              leftSection={<IconPencil size={16} />}
              disabled={!meetingAgenda.id || !meetingAgenda.clientId}
              onClick={handleEditAgenda}
            >
              Edit
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftSection={<IconDownload size={16} />}
              loading={isDownloading}
              onClick={handleDownload}
            >
              Download .docx
            </Button>
          </Group>
        }
      >
        <MarkdownEditor
          rootClass="compact"
          defaultValue={meetingAgenda.markdown}
          readonly
          bare
          bg="transparent"
          p={0}
          bd="none"
        />
      </MeetingDetailsCard>
    );
  }

  if (isProcessing) {
    return (
      <MeetingDetailsCard title="Meeting agenda">
        <Flex
          align="center"
          gap="xxs"
          bg="brand.0"
          pl="xs"
          pr="sm"
          py="sm"
          style={{ borderRadius: 20 }}
        >
          <Loader size={20} />
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black">
            Generating the meeting agenda
          </Text>
        </Flex>
      </MeetingDetailsCard>
    );
  }

  return (
    <MeetingDetailsCard title="Meeting agenda">
      <Stack gap="xs">
        <AgendaPill
          label={`Meeting agenda based only on ${mainMemberName}'s data`}
          onGenerate={handleGenerateIndividual}
          disabled={!defaultTemplate}
        />
        <AgendaPill
          label={
            <>
              Meeting agenda based on{' '}
              <Text
                component="span"
                size="textSm"
                fw={FONT_WEIGHTS.medium}
                c="black"
                style={{ textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
              >
                {familyName} members
              </Text>
            </>
          }
          trailing={
            <IconPencil
              size={16}
              color="var(--mantine-color-gray-10)"
              style={{ cursor: 'pointer', flexShrink: 0 }}
              onClick={onEditMembers}
            />
          }
          onGenerate={() => onGenerateFamily?.()}
        />
      </Stack>
    </MeetingDetailsCard>
  );
}

function AgendaPill({
  label,
  trailing,
  onGenerate,
  disabled,
}: {
  label: ReactNode;
  trailing?: ReactNode;
  onGenerate: () => void;
  disabled?: boolean;
}) {
  return (
    <Flex
      justify="space-between"
      align="center"
      gap="sm"
      bg="brand.0"
      p="sm"
      style={{ borderRadius: 20 }}
    >
      <Flex align="center" gap="xxxs" style={{ minWidth: 0 }}>
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black">
          {label}
        </Text>
        {trailing}
      </Flex>
      <Button
        variant="primary"
        size="xs"
        w={70}
        h={24}
        px={8}
        py={3}
        fw={FONT_WEIGHTS.semibold}
        disabled={disabled}
        style={{ flexShrink: 0, borderRadius: 8 }}
        onClick={onGenerate}
      >
        Generate
      </Button>
    </Flex>
  );
}
